import { supabase } from '../lib/supabase';
import { UseMapVisualizationReturn, PerStatVisualizationType, ScatterShape, TrajectoryStyle } from '../hooks/useMapVisualization';
import { TeamShapeConfig } from '../hooks/useTeamShape';
import { VisualizationFilters } from '../components/pitch/types';

export type TacticsImageStatus = 'saved' | 'uploaded';

export interface VisualizationConfig {
  // Visualization type and settings
  visualizationType: string[]; // ['scatter', 'heatmap', etc.]
  pointSelection: string[]; // ['source', 'destination']
  trajectoryDensity: string; // 'all' | 'medium' | 'low' | 'first_last_only'
  colorBy: 'team' | 'stat_type';
  statTypeVisualizationMap: Record<string, PerStatVisualizationType>;
  
  // Filters - use VisualizationFilters type but store as JSONB compatible format
  filters: VisualizationFilters;
  
  // View settings
  normalizeToFirstHalf: boolean;
  compareMode: boolean;
  homeTeamAttackingDirection: 'left' | 'right';
  awayTeamAttackingDirection: 'left' | 'right';
  whiteBackground: boolean;
  
  // Heatmap settings
  heatmapRadius?: number;
  heatmapSmoothness?: number;
  heatmapOpacity?: number;
  
  // Grid settings
  gridSize?: number;
  gridShowLabels?: boolean;
  gridOpacity?: number;
  
  // Scatter and trajectory customization
  scatterShapes?: Record<string, ScatterShape>;
  trajectoryStyles?: Record<string, TrajectoryStyle>;
  
  // Colors
  teamColors?: Record<string, string>;
  statTypeColors?: Record<string, string>;
  
  // Team shape config (if applicable)
  teamShapeConfig?: TeamShapeConfig;
  
  // Team info (for display)
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName: string;
  awayTeamName: string;
}

export interface TacticsImage {
  id: string;
  match_id: string;
  tournament_id: string;
  name: string;
  visualization_config: VisualizationConfig;
  view_types: string[];
  status: TacticsImageStatus;
  image_url?: string | null;
  image_storage_path?: string | null;
  compare_mode: boolean;
  image_format?: string | null;
  file_size_bytes?: number | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface CreateTacticsImageInput {
  matchId: string;
  tournamentId: string;
  name: string;
  visualizationConfig: VisualizationConfig;
  viewTypes: string[];
  compareMode: boolean;
  createdBy: string;
}

export interface UpdateTacticsImageInput {
  name?: string;
  visualizationConfig?: VisualizationConfig;
  viewTypes?: string[];
  compareMode?: boolean;
  updatedBy: string;
}

export interface UploadTacticsImageInput {
  imageUrl: string;
  imageStoragePath: string;
  imageFormat: 'webp' | 'jpeg';
  fileSizeBytes: number;
  updatedBy: string;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

const table = 'tactics_images';

export const tacticsImagesService = {
  /**
   * Get all tactics images for a match
   */
  async getTacticsImagesByMatchId(matchId: string): Promise<ServiceResult<TacticsImage[]>> {
    try {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('match_id', matchId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tactics images:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: (data as TacticsImage[]) || [] };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('getTacticsImagesByMatchId failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Get a single tactics image by ID
   */
  async getTacticsImageById(id: string): Promise<ServiceResult<TacticsImage>> {
    try {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching tactics image:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as TacticsImage };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('getTacticsImageById failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Create a new tactics image (metadata only, status='saved')
   */
  async createTacticsImage(input: CreateTacticsImageInput): Promise<ServiceResult<TacticsImage>> {
    try {
      const { data, error } = await supabase
        .from(table)
        .insert({
          match_id: input.matchId,
          tournament_id: input.tournamentId,
          name: input.name,
          visualization_config: input.visualizationConfig,
          view_types: input.viewTypes,
          compare_mode: input.compareMode,
          status: 'saved',
          created_by: input.createdBy,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating tactics image:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as TacticsImage };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('createTacticsImage failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Update tactics image (only allowed if status='saved')
   */
  async updateTacticsImage(id: string, updates: UpdateTacticsImageInput): Promise<ServiceResult<TacticsImage>> {
    try {
      // First check if image exists and get its status
      const { data: existing, error: fetchError } = await supabase
        .from(table)
        .select('status')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (fetchError) {
        return { success: false, error: fetchError.message };
      }

      const isUploaded = existing?.status === 'uploaded';
      
      // For uploaded images, only allow name updates
      if (isUploaded) {
        const hasNonNameUpdates = 
          updates.visualizationConfig !== undefined ||
          updates.viewTypes !== undefined ||
          updates.compareMode !== undefined;
        
        if (hasNonNameUpdates) {
          return { success: false, error: 'Cannot update configuration of uploaded tactics image. Only name can be updated.' };
        }
        
        // Only name update is allowed for uploaded images
        if (updates.name === undefined) {
          return { success: false, error: 'No valid updates provided' };
        }
      }

      const payload: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.name !== undefined) payload.name = updates.name;
      
      // Only allow these updates for 'saved' status images
      if (!isUploaded) {
        if (updates.visualizationConfig !== undefined) payload.visualization_config = updates.visualizationConfig;
        if (updates.viewTypes !== undefined) payload.view_types = updates.viewTypes;
        if (updates.compareMode !== undefined) payload.compare_mode = updates.compareMode;
      }

      // Build the update query
      let query = supabase
        .from(table)
        .update(payload)
        .eq('id', id)
        .eq('is_active', true);

      // For saved images, add extra safety check
      if (!isUploaded) {
        query = query.eq('status', 'saved');
      }

      const { data, error } = await query
        .select()
        .single();

      if (error) {
        console.error('Error updating tactics image:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as TacticsImage };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('updateTacticsImage failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Upload tactics image (update with image URL and change status to 'uploaded')
   */
  async uploadTacticsImage(id: string, uploadData: UploadTacticsImageInput): Promise<ServiceResult<TacticsImage>> {
    try {
      const payload: Record<string, unknown> = {
        status: 'uploaded',
        image_url: uploadData.imageUrl,
        image_storage_path: uploadData.imageStoragePath,
        image_format: uploadData.imageFormat,
        file_size_bytes: uploadData.fileSizeBytes,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from(table)
        .update(payload)
        .eq('id', id)
        .eq('is_active', true)
        .select()
        .single();

      if (error) {
        console.error('Error uploading tactics image:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data as TacticsImage };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('uploadTacticsImage failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Delete tactics image (soft delete)
   */
  async deleteTacticsImage(id: string, userId: string): Promise<ServiceResult<void>> {
    try {
      const { error } = await supabase
        .from(table)
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) {
        console.error('Error deleting tactics image:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('deleteTacticsImage failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Get tournament ID from match
   */
  async getMatchTournamentId(matchId: string): Promise<ServiceResult<string>> {
    try {
      const { data, error } = await supabase
        .from('matches')
        .select('tournament_id')
        .eq('id', matchId)
        .single();

      if (error) {
        console.error('Error fetching match tournament:', error);
        return { success: false, error: error.message };
      }

      if (!data?.tournament_id) {
        return { success: false, error: 'Match does not have a tournament_id' };
      }

      return { success: true, data: data.tournament_id };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('getMatchTournamentId failed:', message);
      return { success: false, error: message };
    }
  },

  /**
   * Build storage path for tactics image
   * Format: tactics/{tournamentId}/{matchId}/{imageId}/image.webp
   */
  buildStoragePath(tournamentId: string, matchId: string, imageId: string, format: 'webp' | 'jpeg'): string {
    return `tactics/${tournamentId}/${matchId}/${imageId}/image.${format}`;
  },

  /**
   * Extract visualization config from mapVisualization hook state
   */
  extractVisualizationConfig(
    mapVisualization: UseMapVisualizationReturn,
    homeTeamId: string,
    awayTeamId: string,
    homeTeamName: string,
    awayTeamName: string,
    teamShapeConfig?: TeamShapeConfig
  ): VisualizationConfig {
    return {
      visualizationType: mapVisualization.visualizationType,
      pointSelection: mapVisualization.pointSelection,
      trajectoryDensity: mapVisualization.trajectoryDensity,
      colorBy: mapVisualization.colorBy,
      statTypeVisualizationMap: mapVisualization.statTypeVisualizationMap,
      filters: mapVisualization.filters,
      normalizeToFirstHalf: mapVisualization.normalizeToFirstHalf,
      compareMode: mapVisualization.compareMode,
      homeTeamAttackingDirection: mapVisualization.homeTeamAttackingDirection,
      awayTeamAttackingDirection: mapVisualization.awayTeamAttackingDirection,
      whiteBackground: mapVisualization.whiteBackground,
      heatmapRadius: mapVisualization.heatmapRadius,
      heatmapSmoothness: mapVisualization.heatmapSmoothness,
      heatmapOpacity: mapVisualization.heatmapOpacity,
      gridSize: mapVisualization.gridSize,
      gridShowLabels: mapVisualization.gridShowLabels,
      gridOpacity: mapVisualization.gridOpacity,
      scatterShapes: mapVisualization.scatterShapes,
      trajectoryStyles: mapVisualization.trajectoryStyles,
      teamColors: mapVisualization.teamColors,
      statTypeColors: mapVisualization.statTypeColors,
      teamShapeConfig: teamShapeConfig,
      homeTeamId,
      awayTeamId,
      homeTeamName,
      awayTeamName,
    };
  },

  /**
   * Extract view types from visualization config
   */
  extractViewTypes(visualizationType: string[]): string[] {
    // Filter out 'both' and return unique types
    const types = visualizationType.filter(type => type !== 'both');
    return [...new Set(types)];
  },
};

